/**
 * The ProductUI namespace context is used to provide a namespace to the ProductUI components
 * when an override is necessary. For example, a scene has a default theme, such as a real estate
 * company, but on a certain page, the theme is going to be construction instead. The namespace
 * context provider allows us to wrap a section with a namespace and any ProductUI scene that wants
 * to use that namespace can add support
 *
 * If you want your ProductUI scene to support the custom namespace it should use the translation hook
 * like so
 *
 * const { t } = useProductUINSTranslation()
 *
 */

import { createContext, useContext } from 'react'

import { useLocale } from 'next-intl'

import type { Transifex } from 'types'

import { useTranslation } from '@/app/i18n/client'

type ProductUINSContextType = string

type ProductUINSProviderProps = {
    children: React.ReactNode
    ns: Transifex.Namespaces
}

const ProductUINSContext = createContext<ProductUINSContextType | undefined>(undefined)

export function ProductUINSProvider({ children, ns }: ProductUINSProviderProps) {
    return <ProductUINSContext.Provider value={ns}>{children}</ProductUINSContext.Provider>
}

export function useProductUINSContext(): ProductUINSContextType {
    const ctx = useContext(ProductUINSContext)

    if (!ctx) {
        return ''
    }

    return ctx
}

export function useProductUINSTranslation() {
    const locale = useLocale()
    const ns = useProductUINSContext()
    const { t } = useTranslation(locale, [ns, 'productui'], 'fallback')

    return { t }
}
